/**
 * The ledger, rendered from `ledger.json`: the four sections in order (M3.2),
 * the card (M3.3) with its Trace sheet (M3.5) and its section's actions
 * (M3.6b), uncertainty in words. No meters, no message list: a card's
 * evidence opens from its Trace button and nowhere else.
 */
import { Fragment, useState } from 'react'
import type { Evidence, EvidenceId, Ledger, LedgerItem, LedgerItemId, Party } from '@cordelia/core'
import { Button, Card, CardActions, Section, Trace } from '@cordelia/ui'
import type { CardAction, CardLinks } from '@cordelia/ui'
import { emptyFacts, ledgerEmptyLines, sectionEmptyLine } from './empty.js'
import { cardDomId, PROBABLY_DONE_CAP, sectionsOf } from './sections.js'
import { actionsFor, sectionOf } from '../../actions/catalog.js'
import type { ActionSpec } from '../../actions/catalog.js'
import { NO_THREAD, threadLinkFor } from '../../actions/links.js'
import { resolveParty } from '../../actions/perform.js'
import type { LocalRouting } from '../../actions/routing.js'
import { Scoring } from '../../study/Scoring.js'
import type { Judgment, ScoreField } from '../../study/fields.js'
import styles from './Ledger.module.scss'

export type LedgerViewProps = {
  readonly ledger: Ledger
  readonly cutoff: string
  readonly windowDays: number
  readonly messagesRead: number
  readonly probablyDoneCap?: number
  /** Study mode: judgments per card and where to send a change. Absent when off. */
  readonly scoring?: ScoringHooks
  /** Section actions (M3.6b). Absent in a read-only render, such as a test. */
  readonly actions?: ActionHooks
}

export type ScoringHooks = {
  readonly scores: ReadonlyMap<string, Judgment>
  readonly onScore: (item_id: string, field: ScoreField, value: string) => void
  readonly scored: number
}

export type ActionHooks = {
  /** Where the appended log puts each card until recomputation runs (M4.3). */
  readonly routing: ReadonlyMap<LedgerItemId, LocalRouting>
  /** Cards whose append is in flight. */
  readonly recomputing: ReadonlySet<LedgerItemId>
  readonly onAction: (item: LedgerItem, spec: ActionSpec, party?: Party) => void
  readonly onUndo: (item: LedgerItem, routing: LocalRouting) => void
  /** Correct opens the correction sheet (M4.1). Absent until it lands. */
  readonly onCorrect?: ((item: LedgerItem) => void) | undefined
  /** Why Correct is unavailable while the sheet is absent. */
  readonly correctUnavailable?: string | undefined
  /** Set when this browser refused a write, so nothing was recorded. */
  readonly error?: string | null | undefined
}

const HISTORY_NOTE =
  'Real commitments whose horizons passed without a message confirming them. Were they done?'

/** The question Delegate asks. An organization makes it the organization's. */
const delegatePrompt = (item: LedgerItem, ledger: Ledger): string => {
  const organizational =
    item.promise_scope === 'organizational' || item.promise_scope === 'collective'
  const org = ledger.identity.organizations.find((o) => o.id === item.owed_by)
  return organizational && org?.name ? `Hand this to someone at ${org.name}` : 'Who has this now?'
}

type Ctx = {
  readonly ledger: Ledger
  readonly cutoff: string
  readonly evidenceById: ReadonlyMap<EvidenceId, Evidence>
  readonly links: CardLinks
  readonly actions?: ActionHooks | undefined
}

/** Deep links into Gmail: the thread behind a piece of evidence, the draft behind a signal. */
function linksFor(ledger: Ledger): CardLinks {
  const threadOf = new Map(ledger.sources.map((s) => [s.source_id, s.provider_thread_id]))
  return {
    source: (evidence) => {
      const thread = threadOf.get(evidence.source_id) ?? null
      return thread ? `https://mail.google.com/mail/u/0/#all/${thread}` : null
    },
    draft: (draft) =>
      draft.provider_draft_id
        ? `https://mail.google.com/mail/u/0/#drafts/${draft.provider_draft_id}`
        : null,
  }
}

/**
 * The section's actions for one card, from the table (M3.6a) and nowhere
 * else. A deep link is a link; everything else appends to the correction log.
 * Nothing here sends: there is no compose, no reply body, no draft.
 */
function cardActions(ctx: Ctx, item: LedgerItem): readonly CardAction[] {
  const hooks = ctx.actions
  const section = sectionOf(item.surface)
  if (!hooks || section === null) return []
  const href = threadLinkFor(ctx.ledger, item)
  const busy = hooks.recomputing.has(item.id)

  return actionsFor(section).map((spec): CardAction => {
    const base = {
      // A section can offer one verb twice with two reasons (Dismiss), so the
      // key is the verb and its label.
      id: `${spec.id}:${spec.label}`,
      label: spec.label,
      busy,
      overflow: spec.overflow,
      ...(spec.destructive === true ? { destructive: true as const } : {}),
    }
    if (spec.kind === 'deep_link')
      return {
        ...base,
        kind: 'link',
        href,
        reason: NO_THREAD,
        onSelect: () => hooks.onAction(item, spec),
      }
    if (spec.asksForParty === true)
      return {
        ...base,
        kind: 'prompt',
        prompt: delegatePrompt(item, ctx.ledger),
        placeholder: 'Name or address',
        confirm: spec.label,
        // The log carries a party, never typed text: anything the identity
        // graph does not know stays `unresolved`.
        onSubmit: (value) => hooks.onAction(item, spec, resolveParty(value, ctx.ledger)),
      }
    if (spec.opensCorrectionSheet === true)
      return {
        ...base,
        kind: 'command',
        disabled: hooks.onCorrect === undefined,
        reason: hooks.correctUnavailable,
        onSelect: () => hooks.onCorrect?.(item),
      }
    return { ...base, kind: 'command', onSelect: () => hooks.onAction(item, spec) }
  })
}

/**
 * What the card says after you act on it: what you did, that the ledger will
 * be recomputed, and the way back. There is no delete — taking it back
 * appends a later correction, and both stay in Trace.
 */
function Acknowledgement({
  ctx,
  item,
  routing,
}: {
  ctx: Ctx
  item: LedgerItem
  routing: LocalRouting
}) {
  const busy = ctx.actions?.recomputing.has(item.id) === true
  return (
    <p className={styles.acted}>
      <span>{busy ? 'Recomputing…' : routing.note}</span>
      {routing.undo !== null && !busy && ctx.actions ? (
        <Button variant="quiet" size="sm" onClick={() => ctx.actions?.onUndo(item, routing)}>
          Undo
        </Button>
      ) : null}
    </p>
  )
}

/** One item as the card, given exactly what the ledger holds for it, with its Trace. */
function ItemCard({
  ctx,
  item,
  scoring,
}: {
  ctx: Ctx
  item: LedgerItem
  scoring?: ScoringHooks | undefined
}) {
  const { ledger } = ctx
  const [expanded, setExpanded] = useState(false)
  const claim = ledger.claims.find((c) => c.id === item.claim_id)
  const evidence = (claim?.evidence_ids ?? [])
    .map((id) => ctx.evidenceById.get(id))
    .filter((e): e is Evidence => e !== undefined)
  const routing = ctx.actions?.routing.get(item.id)
  const actions = cardActions(ctx, item)
  return (
    <Card
      id={cardDomId(item)}
      item={item}
      claim={claim}
      evidence={evidence}
      drafts={ledger.draft_signals.filter((d) => d.item_id === item.id)}
      replies={ledger.reply_owed_signals.filter((r) => r.item_id === item.id)}
      // The signal row and the scope badge already say these two.
      whyNow={item.attention_reasons.filter(
        (r) => !r.startsWith('Signal:') && !r.startsWith('On behalf of'),
      )}
      identity={ledger.identity}
      links={ctx.links}
      expanded={expanded}
      // Once you have acted, the row is what you did and how to take it back;
      // the section's verbs come back when the next run has recomputed.
      actions={
        routing ? (
          <Acknowledgement ctx={ctx} item={item} routing={routing} />
        ) : actions.length > 0 ? (
          <CardActions actions={actions} />
        ) : undefined
      }
      trace={
        <Trace
          ledger={ledger}
          item={item}
          cutoff={ctx.cutoff}
          links={ctx.links}
          onOpenChange={setExpanded}
        />
      }
    >
      {scoring && (
        <Scoring
          item={item}
          value={scoring.scores.get(item.id)}
          onScore={(field, value) => scoring.onScore(item.id, field, value)}
        />
      )}
    </Card>
  )
}

/** A card acted on this session: one line where it was, never a disappearance. */
function SettledLine({ ctx, item }: { ctx: Ctx; item: LedgerItem }) {
  const routing = ctx.actions?.routing.get(item.id)
  const busy = ctx.actions?.recomputing.has(item.id) === true
  if (!routing) return null
  return (
    <p className={styles.settled} data-item={item.id}>
      <span className={styles.settledText}>
        {item.proposition} — {busy ? 'Recomputing…' : routing.note}
      </span>
      {routing.undo !== null && !busy && ctx.actions ? (
        <Button variant="quiet" size="sm" onClick={() => ctx.actions?.onUndo(item, routing)}>
          Undo
        </Button>
      ) : null}
    </p>
  )
}

export function LedgerView({
  ledger,
  cutoff,
  windowDays,
  messagesRead,
  probablyDoneCap = PROBABLY_DONE_CAP,
  scoring,
  actions,
}: LedgerViewProps) {
  const ctx: Ctx = {
    ledger,
    cutoff,
    evidenceById: new Map(ledger.evidence.map((e) => [e.id, e])),
    links: linksFor(ledger),
    actions,
  }
  const sections = sectionsOf(ledger, probablyDoneCap, actions?.routing)
  const total = sections.reduce((n, s) => n + s.items.length, 0)
  const probablyDone = sections.find((s) => s.id === 'probably-done')?.items.length ?? 0
  // Empty states (M3.7): reassurance with the evidence behind it, never an
  // all-clear. The facts are read from this run and nowhere else.
  const facts = emptyFacts({ ledger, cutoff, windowDays, messagesRead })
  const counts = (id: (typeof sections)[number]['id']): number =>
    sections.find((section) => section.id === id)?.items.length ?? 0

  return (
    <div className={styles.root}>
      <header className={styles.masthead}>
        <h1>Your ledger</h1>
        <p>
          As of {cutoff}. Read {messagesRead} messages from the last {windowDays} days. Every quote
          is verbatim from a message you sent or received. Nothing left this browser.
        </p>
        <p className={styles.counts}>
          You owe {counts('you-owe')} · You're waiting on {counts('youre-waiting-on')} · May be
          expected {counts('may-be-expected')} · Probably done? {probablyDone}
          {facts.resolved > 0 ? ` · ${facts.resolved} resolved along the way` : ''}
        </p>
        {actions?.error !== undefined && actions.error !== null && (
          <p className={styles.counts} role="status">
            {actions.error}
          </p>
        )}
        {scoring && (
          <p className={styles.counts}>
            {scoring.scored === 0
              ? 'Nothing scored yet'
              : `${scoring.scored} card${scoring.scored === 1 ? '' : 's'} scored — saved in this browser`}
            . Scores stay in this browser until you export them; the export contains no message
            text.
          </p>
        )}
        {total === 0 && (
          <div className={styles.emptyLedger}>
            {ledgerEmptyLines(facts).map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
        )}
      </header>
      {sections.map((section) => (
        <Fragment key={section.id}>
          {section.id === 'probably-done' && <hr className={styles.fold} />}
          <Section
            title={section.label}
            count={section.items.length}
            empty={sectionEmptyLine(section.id, facts)}
            hasContent={section.settled.length > 0}
            note={section.id === 'probably-done' ? HISTORY_NOTE : undefined}
            footnote={section.queued > 0 ? `${section.queued} more in history.` : undefined}
          >
            {section.items.map((item) => (
              <ItemCard key={item.id} ctx={ctx} item={item} scoring={scoring} />
            ))}
            {section.settled.map((item) => (
              <SettledLine key={item.id} ctx={ctx} item={item} />
            ))}
          </Section>
        </Fragment>
      ))}
      <footer className={styles.foot}>
        Reconstructed in this browser from your Takeout export. Drafts appear only as signals; they
        are never evidence.
      </footer>
    </div>
  )
}
